import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, NgModule, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { dateCompare } from '@core/services/date-range';
import { TranslateModule } from '@ngx-translate/core';
import { PipesModule } from '@shared/pipes/pipes.module';
import { Chart, ChartOptions } from 'chart.js';
import { OwlDateTimeModule } from 'ng-pick-datetime-ex';
import { ChartsModule } from 'ng2-charts';
import { Observable } from 'rxjs';

export interface dateRangeE {
  name: string;
  value: number;
}
export interface GetTop20SellingItemsByReps {
  productName: string;
  totalSales: number;
  productId: string;
  productNumber: string;
}

export interface GetTotalSalesReportDto {
  repsId: string;
  startDate: '';
  endDate: '';
}

export interface GetTotalSalesReportRes {
  totalOrders: number;
  activeCustomers: number;
  averageOrderAmount: number;
  totalsales: Totalsales[];
}

export interface Totalsales {
  month: string;
  totalSales: number;
}
@Component({
  selector: 'app-mobile-web-view-reports',
  templateUrl: './mobile-web-view-reports.component.html',
  styleUrls: ['./mobile-web-view-reports.component.scss'],
})
export class MobileWebViewReportsComponent implements OnInit {
  minDate: Date = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate()
  );

  selectionDateForm: FormGroup = <FormGroup>{};
  public barChartOptions: ChartOptions = {
    responsive: true,
  };
  repsId: string = '';
  top20SellingData: GetTop20SellingItemsByReps[] = [];
  selectionList: dateRangeE[] = [
    { name: '1 to 10 Items', value: 10 },
    { name: '1 to 20 Items', value: 20 },
  ];
  dateRangeControl: FormControl = new FormControl('');
  chart: Chart | undefined;
  totalSalesChart: Chart | undefined;
  totalSalesData: GetTotalSalesReportRes = <GetTotalSalesReportRes>{};
  totalSalesbar: Totalsales[] = [];
  isLoading: boolean = false;

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private route: ActivatedRoute
  ) {
    this.dateRangeControl.setValue(10);
    this.createRangeForm();
  }

  ngOnInit(): void {
    this.route.params.subscribe((sId) => {
      if (sId && Object.keys(sId).length > 0 && sId['id']) {
        this.repsId = sId['id'];
        const getDto = {
          repsId: this.repsId,
          topCount: this.dateRangeControl.value,
        };
        this.getAllTop20SellingItemsByReps(getDto);
        this.getAllTotalSalesReport(this.repsId);
      }
    });
  }

  // Define a plugin to display a "No data available" message
  noDataPlugin = {
    id: 'noDataPlugin',
    beforeDraw: (chart) => {
      const ctx = chart.ctx;
      const data = chart.data;

      if (data.datasets.length === 0 || data.datasets[0].data.length === 0) {
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = '16px Arial';
        ctx.fillStyle = '#0069ff';
        const { width, height } = chart;
        ctx.fillText('No data available', width / 2, height / 2);
        ctx.restore();
      }
    },
  };

  private createRangeForm() {
    this.selectionDateForm = this.fb.group(
      {
        fromDate: ['', [Validators.required]],
        toDate: ['', [Validators.required]],
      },
      {
        validators: dateCompare(),
      }
    );
  }

  getRangeData() {
    if (this.selectionDateForm.valid) {
      const rangeData: GetTotalSalesReportDto = {
        repsId: this.repsId,
        startDate: this.selectionDateForm.get('fromDate').value.toDateString(),
        endDate: this.selectionDateForm.get('toDate').value.toDateString(),
      };
      this.getTotalSalesDataReport(rangeData);
    }
  }

  private getAllTotalSalesReport(id: string): void {
    const formData = new Date(
      new Date().setFullYear(new Date().getFullYear() - 1)
    );
    const todate = new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate()
    );
    this.selectionDateForm.get('fromDate').setValue(formData);
    this.selectionDateForm.get('toDate').setValue(todate);
    this.selectionDateForm.updateValueAndValidity();
    const dto: GetTotalSalesReportDto = {
      repsId: id,
      startDate: this.selectionDateForm.get('fromDate').value.toDateString(),
      endDate: this.selectionDateForm.get('toDate').value.toDateString(),
    };
    this.getTotalSalesDataReport(dto);
  }

  private getTotalSalesDataReport(dto: GetTotalSalesReportDto) {
    this.isLoading = true;
    this.getTotalSalesdata(dto).subscribe((res: GetTotalSalesReportRes) => {
      this.isLoading = false;
      if (res) {
        this.totalSalesData = res;
        this.totalSalesbar = res.totalsales;

        if (this.totalSalesbar && this.totalSalesbar.length > 0) {
          if (!this.totalSalesChart) {
            this.totalSalesChart = new Chart('totalSalesChart', {
              type: 'bar',
              plugins: [this.noDataPlugin],
              options: {
                responsive: true,
                legend: {
                  display: false,
                },
                scales: {
                  yAxes: [
                    {
                      ticks: {
                        beginAtZero: true,
                        callback(value, index, values) {
                          return value === 0 ? value : '$' + Number(value);
                        },
                      },
                    },
                  ],
                },
              },
              data: {
                labels: this.totalSalesbar.map((s) => s.month),
                datasets: [
                  {
                    data: this.totalSalesbar.map((s) => s.totalSales),
                    backgroundColor: '#2A6C82',
                    barThickness: 25,
                  },
                ],
              },
            });
          }
        } else {
          this.chart = new Chart('totalSalesChart', {
            type: 'bar',
            plugins: [this.noDataPlugin],
            options: {},
            data: {},
          });
        }
      }
    });
  }

  private getAllTop20SellingItemsByReps(data): void {
    this.isLoading = true;
    this.getTop20SellingItemsByReps(data).subscribe(
      (res: GetTop20SellingItemsByReps[]) => {
        this.isLoading = false;
        if (res && res.length > 0) {
          this.top20SellingData = res.sort(
            (a, b) => b.totalSales - a.totalSales
          );

          // Destroy the previous chart instance if it exists
          if (this.chart) {
            this.chart.destroy();
          }

          this.chart = new Chart('top20Chart', {
            type: 'horizontalBar',
            plugins: [this.noDataPlugin],
            options: {
              responsive: true,
              legend: {
                display: false,
              },
              scales: {
                xAxes: [
                  {
                    ticks: {
                      beginAtZero: false,
                      stepSize: 2000,
                      callback(value, index, values) {
                        return value === 0
                          ? value
                          : Number(value.toLocaleString().split(',')[0]) + 'k';
                      },
                    },
                    position: 'top',
                  },
                ],
              },
            },
            data: {
              labels: this.top20SellingData.map((s) => s.productNumber),
              datasets: [
                {
                  data: this.top20SellingData.map((s) => s.totalSales),
                  barThickness: 15,
                  backgroundColor: '#2A6C82',
                },
              ],
            },
          });
        } else {
          this.top20SellingData = [];

          // Destroy the previous chart instance if it exists
          if (this.chart) {
            this.chart.destroy();
          }

          this.chart = new Chart('top20Chart', {
            type: 'horizontalBar',
            plugins: [this.noDataPlugin],
            options: {},
            data: {},
          });
        }
      }
    );
  }

  private getTop20SellingItemsByReps(
    data
  ): Observable<GetTop20SellingItemsByReps[]> {
    const url = 'MobileView/gettop20sellingitemByReps';
    return this.http.get<GetTop20SellingItemsByReps[]>(url, { params: data });
  }

  private getTotalSalesdata(data: GetTotalSalesReportDto) {
    const url = 'MobileView/GetTotalSalesReport';
    const params = {
      RepsId: data.repsId,
      StartDate: data.startDate,
      EndDate: data.endDate,
    };
    return this.http.get<GetTotalSalesReportRes>(url, { params: params });
  }

  compareFn(obj1: any, obj2: any) {
    return obj1 && obj2 ? obj1.id === obj2.id : obj1 === obj2;
  }

  getData(event: MatSelectChange) {
    if (event) {
      const getDto = {
        repsId: this.repsId,
        topCount: event.value,
      };
      this.getAllTop20SellingItemsByReps(getDto);
    }
  }
}

@NgModule({
  declarations: [MobileWebViewReportsComponent],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    TranslateModule,
    OwlDateTimeModule,
    ChartsModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    PipesModule,
  ],
})
export class MobileWebViewReportsModule {}
